import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PortfolioState, UserPortfolio, CatchMetrics, LoginHistoryList, BlockedList, RecentCatchesResponse, ReferralList, PortfolioSearchUser } from "./types";
import { fetchUserPortfolio, fetchUserCatchMetrics, fetchUserLoginHistory, fetchUserBlockedList, fetchUserRecentCatches, promoteToSponsor, fetchUserReferrals, portfolioSearch } from "./actions";

const initialState: PortfolioState = {
    portfolio: null,
    catchMetrics: null,
    loginHistoryList: null,
    blockedList: null,
    recentCatches: null,
    promotionMessage: null,
    referralList: null,
    portfolioSearch: [],
    loading: "idle",
    catchMetricsLoading: "idle",
    loginHistoryListLoading: "idle",
    blockedListLoading: "idle",
    recentCatchesLoading: "idle",
    promotionLoading: "idle",
    referralListLoading: "idle",
    portfolioSearchLoading: "idle",
    error: null,
    catchMetricsError: null,
    loginHistoryListError: null,
    blockedListError: null,
    recentCatchesError: null,
    promotionError: null,
    referralListError: null,
    portfolioSearchError: null,
};

const portfolioSlice = createSlice({
    name: "portfolio",
    initialState,
    reducers: {
        clearPromotionMessage: (state) => {
            state.promotionMessage = null;
        },
        clearPromotionError: (state) => {
            state.promotionError = null;
        },
        clearPortfolioSearch: (state) => {
            state.portfolioSearch = [];
            state.portfolioSearchLoading = "idle";
            state.portfolioSearchError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUserPortfolio.pending, (state) => {
                state.loading = "pending";
                state.error = null;
            })
            .addCase(fetchUserPortfolio.fulfilled, (state, action: PayloadAction<UserPortfolio>) => {
                state.loading = "succeeded";
                state.portfolio = action.payload;
            })
            .addCase(fetchUserPortfolio.rejected, (state, action: PayloadAction<any>) => {
                state.loading = "failed";
                state.error = action.payload?.message || "Failed to fetch user portfolio";
            })

            .addCase(fetchUserCatchMetrics.pending, (state) => {
                state.catchMetricsLoading = "pending";
                state.catchMetricsError = null;
            })
            .addCase(fetchUserCatchMetrics.fulfilled, (state, action: PayloadAction<CatchMetrics>) => {
                state.catchMetricsLoading = "succeeded";
                state.catchMetrics = action.payload;
            })
            .addCase(fetchUserCatchMetrics.rejected, (state, action: PayloadAction<any>) => {
                state.catchMetricsLoading = "failed";
                state.catchMetricsError = action.payload?.message || "Failed to fetch catch metrics";
            })
            
            .addCase(fetchUserLoginHistory.pending, (state) => {
                state.loginHistoryListLoading = "pending";
                state.loginHistoryListError = null;
            })
            .addCase(fetchUserLoginHistory.fulfilled, (state, action: PayloadAction<LoginHistoryList>) => {
                state.loginHistoryListLoading = "succeeded";
                state.loginHistoryList = action.payload;
            })
            .addCase(fetchUserLoginHistory.rejected, (state, action: PayloadAction<any>) => {
                state.loginHistoryListLoading = "failed";
                state.loginHistoryListError = action.payload?.message || "Failed to fetch login history";
            })

            .addCase(fetchUserBlockedList.pending, (state) => {
                state.blockedListLoading = "pending";
                state.blockedListError = null;
            })
            .addCase(fetchUserBlockedList.fulfilled, (state, action: PayloadAction<BlockedList>) => {
                state.blockedListLoading = "succeeded";
                state.blockedList = action.payload;
            })
            .addCase(fetchUserBlockedList.rejected, (state, action: PayloadAction<any>) => {
                state.blockedListLoading = "failed";
                state.blockedListError = action.payload?.message || "Failed to fetch login history";
            })

            .addCase(fetchUserRecentCatches.pending, (state) => {
                state.recentCatchesLoading = "pending";
                state.recentCatchesError = null;
            })
            .addCase(fetchUserRecentCatches.fulfilled, (state, action: PayloadAction<RecentCatchesResponse>) => {
                state.recentCatchesLoading = "succeeded";
                    state.recentCatches = action.payload;
            })
            .addCase(fetchUserRecentCatches.rejected, (state, action: PayloadAction<any>) => {
                state.recentCatchesLoading = "failed";
                state.recentCatchesError = action.payload?.message || "Failed to fetch login history";
            })
            
            .addCase(promoteToSponsor.pending, (state) => {
                state.promotionLoading = "pending";
                state.promotionError = null;
                state.promotionMessage = null;
            })
            .addCase(promoteToSponsor.fulfilled, (state, action: PayloadAction<{ message: string }>) => {
                state.promotionLoading = "succeeded";
                state.promotionMessage = action.payload.message;
            })
            .addCase(promoteToSponsor.rejected, (state, action: PayloadAction<any>) => {
                state.promotionLoading = "failed";
                state.promotionError = action.payload?.message || "Failed to promote user.";
            })

            .addCase(fetchUserReferrals.pending, (state) => {
                state.referralListLoading = "pending";
                state.referralListError = null;
            })
            .addCase(fetchUserReferrals.fulfilled, (state, action: PayloadAction<ReferralList>) => {
                state.referralListLoading = "succeeded";
                state.referralList = action.payload;
            })
            .addCase(fetchUserReferrals.rejected, (state, action: PayloadAction<any>) => {
                state.referralListLoading = "failed";
                state.referralListError = action.payload?.message || "Failed to fetch login history";
            })
            .addCase(portfolioSearch.pending, (state) => {
                state.portfolioSearchLoading = "pending";
                state.portfolioSearchError = null;
            })
            .addCase(portfolioSearch.fulfilled, (state, action: PayloadAction<PortfolioSearchUser[]>) => {
                state.portfolioSearchLoading = "succeeded";
                state.portfolioSearch = action.payload;
            })
            .addCase(portfolioSearch.rejected, (state, action: PayloadAction<any>) => {
                state.portfolioSearchLoading = "failed";
                state.portfolioSearchError = action.payload?.message || "Failed to fetch users";
            })
    },
});

export const { clearPromotionMessage, clearPromotionError, clearPortfolioSearch } = portfolioSlice.actions;

export default portfolioSlice.reducer;
